/**
 * Onboarding step configuration — the single source of truth for which steps
 * appear for each plan/org-type combination.
 *
 * Step IDs are stable strings (not numbers) so persisted progress remains valid
 * even if the step order changes between plan tiers.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Building,
  Shield,
  UserCheck,
  Users,
  UserPlus,
  Calendar,
  Stethoscope,
  DollarSign,
  Rocket,
} from 'lucide-react';
import {
  getTenantOnboardingStepIdsForTenant,
  type TenantOnboardingOrgType,
  type TenantOnboardingPlanTier,
  type TenantOnboardingStepId,
  type TenantOnboardingStepScopeOptions,
} from '../../../../lib/tenant-onboarding-step-scope';

export type OrgType = TenantOnboardingOrgType;
export type PlanTier = TenantOnboardingPlanTier;

export interface StepDefinition {
  /** Stable string ID — used for progress persistence */
  id: string;
  /** Display label in progress bar */
  label: string;
  /** Icon for progress bar circle */
  icon: LucideIcon;
  /** Whether this step must be completed before proceeding (vs skippable) */
  required: boolean;
  /** Nudge text shown when the user tries to skip (only for non-required steps) */
  skipNudge: string | null;
  /** Short description shown below the step title */
  whyText: string;
}

/** Master step registry — order matters (this is the display order) */
const ALL_STEPS: StepDefinition[] = [
  {
    id: 'welcome',
    label: 'Organisation',
    icon: Building2,
    required: true,
    skipNudge: null,
    whyText:
      "We'll use these details across all your compliance reports and government submissions.",
  },
  {
    id: 'facility',
    label: 'Facility',
    icon: Building,
    required: true,
    skipNudge: null,
    whyText:
      'Your facility is the foundation — residents, staff, and compliance all link back to it.',
  },
  {
    id: 'compliance-config',
    label: 'Compliance',
    icon: Shield,
    required: false,
    skipNudge:
      'Without SIRS notification emails, your team will not be alerted when a Priority 1 incident is reported. You can configure this later in Settings > Notifications.',
    whyText:
      'Under the Aged Care Act 2024, Priority 1 incidents must be reported to the Commission within 24 hours. Without notification emails configured, your team will not be alerted.',
  },
  {
    id: 'key-personnel',
    label: 'Key Personnel',
    icon: UserCheck,
    required: false,
    skipNudge:
      'The Aged Care Act 2024 requires all Key Personnel to be registered with the Commission. You can add them later in Compliance > Responsible Persons.',
    whyText:
      'The Aged Care Act 2024 requires all approved providers to register their Key Personnel — including the CEO, board members, and anyone involved in high-level decisions.',
  },
  {
    id: 'import-clients',
    label: 'Clients',
    icon: Users,
    required: false,
    skipNudge: null,
    whyText:
      'Adding your clients now means you can start logging incidents, creating care plans, and tracking compliance immediately.',
  },
  {
    id: 'invite-team',
    label: 'Team',
    icon: UserPlus,
    required: false,
    skipNudge: null,
    whyText:
      'Inviting your team gives them their own login, permissions, and access to the features they need.',
  },
  {
    id: 'workforce-config',
    label: 'Workforce',
    icon: Calendar,
    required: false,
    skipNudge: 'You can configure shift patterns and screening requirements later in Settings.',
    whyText:
      'Setting up your default shift patterns and worker screening requirements means your roster is ready to go from day one.',
  },
  {
    id: 'clinical-config',
    label: 'Clinical',
    icon: Stethoscope,
    required: false,
    skipNudge:
      'Clinical Care requires guided onboarding to ensure safe implementation. We will be in touch to schedule your setup session.',
    whyText:
      'Configuring medication round times and clinical assessment tools ensures safe, consistent clinical care from day one.',
  },
  {
    id: 'financial-config',
    label: 'Finance',
    icon: DollarSign,
    // Residential go-live needs casemix care-minute targets; the step UI blocks
    // skip for residential/mixed. HOME_CARE-only tenants may still complete
    // without residential targets via the save path.
    required: true,
    skipNudge:
      'Residential facilities must record casemix-adjusted care-minute targets before go-live. Home-care-only tenants can finish funding setup later in Settings or Finance.',
    whyText:
      'Setting up your financial configuration ensures funding claims, billing, and client contributions work correctly. Residential go-live requires casemix-adjusted care-minute targets.',
  },
  {
    id: 'complete',
    label: 'All Done',
    icon: Rocket,
    required: false,
    skipNudge: null,
    whyText: '',
  },
];

/**
 * Returns the visible onboarding steps for a given plan tier and org type.
 * Filters out steps that do not apply to the tenant's plan, care type, and enabled modules.
 */
export function getStepsForTenant(
  plan: PlanTier,
  orgType: OrgType,
  options: TenantOnboardingStepScopeOptions = {},
): StepDefinition[] {
  const visibleStepIds = new Set<TenantOnboardingStepId>(
    getTenantOnboardingStepIdsForTenant(plan, orgType, options),
  );
  return ALL_STEPS.filter((step) => visibleStepIds.has(step.id as TenantOnboardingStepId));
}

/** Terminology helpers based on org type */
export function personLabel(orgType: OrgType): string {
  return orgType === 'RESIDENTIAL'
    ? 'resident'
    : orgType === 'HOME_CARE'
      ? 'client'
      : 'resident/client';
}

export function personLabelPlural(orgType: OrgType): string {
  return orgType === 'RESIDENTIAL'
    ? 'residents'
    : orgType === 'HOME_CARE'
      ? 'clients'
      : 'residents & clients';
}

export function facilityLabel(orgType: OrgType): string {
  return orgType === 'HOME_CARE' ? 'office' : 'facility';
}

export function facilityLabelCapital(orgType: OrgType): string {
  return orgType === 'HOME_CARE' ? 'Office' : 'Facility';
}
